import { inject } from '@angular/core';
import { UtilsService } from '@dxc/data-access';
import { Tab } from '@shared/types';

/**
 * Common tab classes used for styling across tab components
 */
export const tabClasses = {
  page: 'flex min-h-10 flex-wrap items-center justify-between gap-x-4 gap-y-2',
  table:
    'flex min-h-10 flex-wrap items-center justify-between gap-x-4 gap-y-2 relative mb-4 flex gap-x-1 p-0 after:absolute after:inset-x-0 after:bottom-0 after:border-b-2 after:border-border',
  navDiv:
    '[&amp;::-webkit-scrollbar]:h-1 [&amp;::-webkit-scrollbar-thumb]:rounded-full [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&amp;::-webkit-scrollbar-track]:bg-neutral-700 dark:[&amp;::-webkit-scrollbar-thumb]:bg-neutral-500 !-mb-2 -ml-1 flex flex-row overflow-x-auto overflow-y-hidden whitespace-nowrap pb-2 pl-1'
};

/**
 * Determines the variant of the tab based on whether it is the active tab.
 *
 * @param variant - The tab variant style ('page' or 'table')
 * @param tab - The tab to determine the variant for.
 * @param activeTab - The currently active tab
 * @returns hlmBtn variant.
 */
export function getTabVariant(
  variant: 'page' | 'table',
  tab: Tab,
  activeTab: Tab | undefined
): 'outline' | 'link' | 'tab' | 'activeTab' {
  if (variant === 'table') {
    return activeTab?.label === tab.label ? 'activeTab' : 'tab';
  }
  return activeTab?.label === tab.label ? 'outline' : 'link';
}

/**
 * Find a tab by label or default to the first tab
 *
 * @param tabs - Array of tabs
 * @param label - Label to search for
 * @returns The found tab or the first tab in the array
 */
export function findTabByLabelOrDefault(
  tabs: Tab[],
  label: string | undefined
): Tab | undefined {
  if (!tabs.length) return undefined;
  return tabs.find((tab) => tab.label === label) || tabs[0];
}

/**
 * Utility service for tab-related functionality
 */
export class TabsUtils {
  private utilsService = inject(UtilsService);

  /**
   * Format a string using the UtilsService
   *
   * @param str - String to format
   * @returns Formatted string
   */
  formatString(str: string): string {
    return this.utilsService.formatString(str);
  }
}
